import React, { useState, useCallback, useMemo } from 'react'
import axios from 'axios'

// ─── Inline SVG Icons (Feather-style) ──────────────────────

const Ic = ({ children, size = 16, ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {children}
  </svg>
)

const IconBack = (props) => (
  <Ic {...props}>
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </Ic>
)

const IconReply = (props) => (
  <Ic {...props}>
    <polyline points="9 17 4 12 9 7" />
    <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
  </Ic>
)

const IconClipboard = (props) => (
  <Ic {...props}>
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
  </Ic>
)

const IconNote = (props) => (
  <Ic {...props}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </Ic>
)

const IconTask = (props) => (
  <Ic {...props}>
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <polyline points="9 11 12 14 22 4" />
  </Ic>
)

// ─── Utilities ─────────────────────────────────────────────

function formatAge(dateStr) {
  if (!dateStr) return ''
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function formatPhone(num) {
  if (!num) return ''
  const d = String(num).replace(/\D/g, '')
  const n = d.startsWith('1') ? d.slice(1) : d
  if (n.length === 10) return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`
  return num
}

// ─── Type Config ───────────────────────────────────────────

const TYPE_CONFIG = {
  sms: {
    label: 'SMS',
    icon: (props) => (
      <Ic {...props}>
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </Ic>
    ),
    color: 'var(--accent)',
  },
  voicemail: {
    label: 'Voicemail',
    icon: (props) => (
      <Ic {...props}>
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </Ic>
    ),
    color: 'var(--warn)',
  },
  missed_call: {
    label: 'Missed Call',
    icon: (props) => (
      <Ic {...props}>
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3" />
        <polyline points="16 2 22 2 22 8" />
      </Ic>
    ),
    color: 'var(--risk)',
  },
}

// ─── Action Definitions ────────────────────────────────────

const ACTIONS = [
  { key: 'reply', label: 'Reply', icon: IconReply },
  { key: 'work_item', label: 'Work Item', icon: IconClipboard },
  { key: 'note', label: 'Note', icon: IconNote },
  { key: 'task', label: 'Task', icon: IconTask },
]

// ─── Inline Styles ─────────────────────────────────────────

const S = {
  panel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  backRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 16px 6px',
    cursor: 'pointer',
    color: 'var(--ink-3)',
    fontSize: 12,
    fontWeight: 500,
    userSelect: 'none',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '6px 16px 10px',
    borderBottom: '1px solid var(--border)',
  },
  headerIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerInfo: {
    flex: 1,
    minWidth: 0,
  },
  callerName: {
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--ink-1)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  headerMeta: {
    fontSize: 11,
    color: 'var(--ink-3)',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 1,
  },
  typeBadge: {
    fontSize: 9.5,
    fontWeight: 600,
    padding: '1px 6px',
    borderRadius: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  scroll: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 16px 16px',
  },
  contentBlock: {
    background: 'var(--surface-1)',
    borderRadius: 8,
    padding: '12px 14px',
    fontSize: 13,
    lineHeight: 1.55,
    color: 'var(--ink-1)',
    marginBottom: 12,
    border: '1px solid var(--border)',
  },
  matchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    color: 'var(--ink-2)',
    marginBottom: 12,
  },
  matchDot: (ok) => ({
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: ok ? 'var(--ok)' : 'var(--ink-4)',
    flexShrink: 0,
  }),
  projectPicker: {
    marginBottom: 12,
  },
  pickerLabel: {
    fontSize: 11,
    color: 'var(--ink-3)',
    marginBottom: 4,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  actionGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    gap: 8,
    marginBottom: 12,
  },
  actionBtn: (active) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    padding: '10px 4px',
    borderRadius: 8,
    border: active ? '1.5px solid var(--accent)' : '1px solid var(--border)',
    background: active ? 'var(--surface-2)' : 'var(--surface-1)',
    cursor: 'pointer',
    fontSize: 10.5,
    fontWeight: 600,
    color: active ? 'var(--accent)' : 'var(--ink-2)',
    transition: 'all .15s',
  }),
  formSection: {
    background: 'var(--surface-1)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '14px',
  },
  formTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: 'var(--ink-1)',
    marginBottom: 10,
  },
  fieldGroup: {
    marginBottom: 10,
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--ink-3)',
    marginBottom: 3,
    display: 'block',
  },
  resultBox: (ok) => ({
    marginTop: 10,
    padding: '8px 12px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 500,
    background: ok ? 'rgba(56,161,105,.1)' : 'rgba(229,62,62,.1)',
    color: ok ? 'var(--ok)' : 'var(--risk)',
    border: `1px solid ${ok ? 'var(--ok)' : 'var(--risk)'}`,
  }),
}

// ─── Reply Form ────────────────────────────────────────────

function ReplyForm({ item, url, smsNumbers }) {
  const [fromNumber, setFromNumber] = useState(smsNumbers?.[0]?.phoneNumber || '')
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState(null)

  const toNumber = item?.from || ''

  const handleSend = useCallback(async () => {
    if (!fromNumber || !toNumber || !message.trim()) return
    setSending(true)
    setResult(null)
    try {
      const res = await axios.post(`${url}/api/hmi/dashboard/send-sms`, {
        from: fromNumber,
        to: toNumber,
        message: message.trim(),
      })
      setResult({ success: true, data: res.data.data })
      setMessage('')
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.message || err.message })
    } finally {
      setSending(false)
    }
  }, [url, fromNumber, toNumber, message])

  return (
    <div style={S.formSection}>
      <div style={S.formTitle}>Reply via SMS</div>

      <div style={S.fieldGroup}>
        <label style={S.fieldLabel}>From</label>
        <select
          className="sms-select"
          value={fromNumber}
          onChange={(e) => setFromNumber(e.target.value)}
        >
          {(smsNumbers || []).map((n) => (
            <option key={n.id} value={n.phoneNumber}>
              {formatPhone(n.phoneNumber)} — {n.label}
            </option>
          ))}
        </select>
      </div>

      <div style={S.fieldGroup}>
        <label style={S.fieldLabel}>To</label>
        <input
          className="sms-input"
          type="text"
          value={formatPhone(toNumber)}
          readOnly
          style={{ opacity: 0.7, cursor: 'default' }}
        />
      </div>

      <div style={S.fieldGroup}>
        <label style={S.fieldLabel}>Message</label>
        <textarea
          className="sms-textarea"
          rows={4}
          placeholder="Type your reply..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={1000}
        />
      </div>

      <button
        className="dbtn dbtn-accent"
        style={{ width: '100%', height: 36, fontSize: 12.5 }}
        onClick={handleSend}
        disabled={sending || !fromNumber || !toNumber || !message.trim()}
      >
        {sending ? 'Sending...' : 'Send Reply'}
      </button>

      {result && (
        <div style={S.resultBox(result.success)}>
          {result.success
            ? `Sent! Status: ${result.data?.status || 'Queued'}`
            : `Failed: ${result.error}`}
        </div>
      )}
    </div>
  )
}

// ─── Work Item Form ────────────────────────────────────────

function WorkItemForm({ item, itemType, url, resolvedData }) {
  const callerName = item?.fromName || formatPhone(item?.from) || 'Unknown'
  const bodyText = itemType === 'sms' ? item?.text : item?.transcription || ''

  const [title, setTitle] = useState(
    `${itemType === 'voicemail' ? 'VM' : itemType === 'sms' ? 'SMS' : 'Call'} from ${callerName}`
  )
  const [body, setBody] = useState(bodyText || '')
  const [priority, setPriority] = useState('normal')
  const [serviceType, setServiceType] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState(null)

  const handleSubmit = useCallback(async () => {
    if (!title.trim()) return
    setSubmitting(true)
    setResult(null)
    try {
      await axios.post(`${url}/api/hmi/dashboard/comm-action`, {
        action: 'unclaimed_work',
        source: {
          type: itemType,
          from: item?.from || '',
          fromName: item?.fromName || item?.from || '',
          body: item?.text || item?.transcription || '',
          receivedAt: item?.receivedAt || item?.time || '',
          rcId: item?.rcId || item?.id || ''
        },
        title: title.trim(),
        body: body.trim(),
        priority,
        serviceType: serviceType || undefined,
      })
      setResult({ success: true })
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.message || err.message })
    } finally {
      setSubmitting(false)
    }
  }, [url, item, itemType, resolvedData, title, body, priority, serviceType])

  return (
    <div style={S.formSection}>
      <div style={S.formTitle}>Create Unclaimed Work Item</div>

      <div style={S.fieldGroup}>
        <label style={S.fieldLabel}>Title</label>
        <input
          className="sms-input"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div style={S.fieldGroup}>
        <label style={S.fieldLabel}>Body</label>
        <textarea
          className="sms-textarea"
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
        <div>
          <label style={S.fieldLabel}>Priority</label>
          <select
            className="sms-select"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          >
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
        <div>
          <label style={S.fieldLabel}>Service Type</label>
          <select
            className="sms-select"
            value={serviceType}
            onChange={(e) => setServiceType(e.target.value)}
          >
            <option value="">— Select —</option>
            <option value="maintenance">Maintenance</option>
            <option value="irrigation">Irrigation</option>
            <option value="herbicide">Herbicide</option>
            <option value="enhancement">Enhancement</option>
            <option value="install">Install</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      <button
        className="dbtn dbtn-accent"
        style={{ width: '100%', height: 36, fontSize: 12.5 }}
        onClick={handleSubmit}
        disabled={submitting || !title.trim()}
      >
        {submitting ? 'Creating...' : 'Create Work Item'}
      </button>

      {result && (
        <div style={S.resultBox(result.success)}>
          {result.success ? 'Work item created successfully' : `Failed: ${result.error}`}
        </div>
      )}
    </div>
  )
}

// ─── Project Note Form ─────────────────────────────────────

function ProjectNoteForm({ item, itemType, url, resolvedData }) {
  const projects = resolvedData?.projects || []
  const bodyText = itemType === 'sms' ? item?.text : item?.transcription || ''

  const [projectId, setProjectId] = useState(projects.length === 1 ? projects[0]._id : '')
  const [noteKind, setNoteKind] = useState('general')
  const [body, setBody] = useState(bodyText || '')
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState(null)

  const disabled = projects.length === 0

  const handleSubmit = useCallback(async () => {
    if (!projectId || !body.trim()) return
    setSubmitting(true)
    setResult(null)
    try {
      await axios.post(`${url}/api/hmi/dashboard/comm-action`, {
        action: 'project_note',
        source: {
          type: itemType,
          from: item?.from || '',
          fromName: item?.fromName || item?.from || '',
          body: item?.text || item?.transcription || '',
          receivedAt: item?.receivedAt || item?.time || '',
          rcId: item?.rcId || item?.id || ''
        },
        projectId,
        noteKind,
        body: body.trim(),
      })
      setResult({ success: true })
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.message || err.message })
    } finally {
      setSubmitting(false)
    }
  }, [url, item, itemType, resolvedData, projectId, noteKind, body])

  return (
    <div style={S.formSection}>
      <div style={S.formTitle}>Add Project Note</div>

      {disabled ? (
        <div style={{ fontSize: 12, color: 'var(--ink-3)', padding: '8px 0', fontStyle: 'italic' }}>
          No projects matched for this caller. Resolve a contact with project associations first.
        </div>
      ) : (
        <>
          <div style={S.fieldGroup}>
            <label style={S.fieldLabel}>Project</label>
            <select
              className="sms-select"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              {projects.length > 1 && <option value="">— Select project —</option>}
              {projects.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.name || p.projectName || p._id}
                </option>
              ))}
            </select>
          </div>

          <div style={S.fieldGroup}>
            <label style={S.fieldLabel}>Note Kind</label>
            <select
              className="sms-select"
              value={noteKind}
              onChange={(e) => setNoteKind(e.target.value)}
            >
              <option value="customer">Customer</option>
              <option value="general">General</option>
              <option value="scope">Scope</option>
              <option value="pricing">Pricing</option>
              <option value="vendor">Vendor</option>
              <option value="internal">Internal</option>
            </select>
          </div>

          <div style={S.fieldGroup}>
            <label style={S.fieldLabel}>Note</label>
            <textarea
              className="sms-textarea"
              rows={3}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>

          <button
            className="dbtn dbtn-accent"
            style={{ width: '100%', height: 36, fontSize: 12.5 }}
            onClick={handleSubmit}
            disabled={submitting || !projectId || !body.trim()}
          >
            {submitting ? 'Saving...' : 'Add Note'}
          </button>

          {result && (
            <div style={S.resultBox(result.success)}>
              {result.success ? 'Note added successfully' : `Failed: ${result.error}`}
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ─── Create Task Form ──────────────────────────────────────

function CreateTaskForm({ item, itemType, url, resolvedData }) {
  const callerName = item?.fromName || formatPhone(item?.from) || 'Unknown'
  const projects = resolvedData?.projects || []

  const [projectId, setProjectId] = useState(projects.length === 1 ? projects[0]._id : '')
  const [title, setTitle] = useState(`Follow up: ${callerName}`)
  const [dueDate, setDueDate] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState(null)

  const handleSubmit = useCallback(async () => {
    if (!title.trim()) return
    setSubmitting(true)
    setResult(null)
    try {
      await axios.post(`${url}/api/hmi/dashboard/comm-action`, {
        action: 'create_task',
        source: {
          type: itemType,
          from: item?.from || '',
          fromName: item?.fromName || item?.from || '',
          body: item?.text || item?.transcription || '',
          receivedAt: item?.receivedAt || item?.time || '',
          rcId: item?.rcId || item?.id || ''
        },
        projectId: projectId || undefined,
        title: title.trim(),
        body: item?.text || item?.transcription || '',
        dueDate: dueDate || undefined,
      })
      setResult({ success: true })
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.message || err.message })
    } finally {
      setSubmitting(false)
    }
  }, [url, item, itemType, resolvedData, projectId, title, dueDate])

  return (
    <div style={S.formSection}>
      <div style={S.formTitle}>Create Task</div>

      {projects.length > 0 && (
        <div style={S.fieldGroup}>
          <label style={S.fieldLabel}>Project (optional)</label>
          <select
            className="sms-select"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="">— No project —</option>
            {projects.map((p) => (
              <option key={p._id} value={p._id}>
                {p.name || p.projectName || p._id}
              </option>
            ))}
          </select>
        </div>
      )}

      <div style={S.fieldGroup}>
        <label style={S.fieldLabel}>Title</label>
        <input
          className="sms-input"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div style={S.fieldGroup}>
        <label style={S.fieldLabel}>Due Date</label>
        <input
          className="sms-input"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />
      </div>

      <button
        className="dbtn dbtn-accent"
        style={{ width: '100%', height: 36, fontSize: 12.5 }}
        onClick={handleSubmit}
        disabled={submitting || !title.trim()}
      >
        {submitting ? 'Creating...' : 'Create Task'}
      </button>

      {result && (
        <div style={S.resultBox(result.success)}>
          {result.success ? 'Task created successfully' : `Failed: ${result.error}`}
        </div>
      )}
    </div>
  )
}

// ─── Main Panel ────────────────────────────────────────────

export default function CommActionPanel({
  item,
  itemType,
  url,
  resolvedData,
  smsNumbers,
  onBack,
}) {
  const [activeAction, setActiveAction] = useState(null)

  const typeConf = TYPE_CONFIG[itemType] || TYPE_CONFIG.sms
  const TypeIcon = typeConf.icon

  // Content body based on type
  const contentBody = useMemo(() => {
    if (itemType === 'sms') return item?.text || ''
    if (itemType === 'voicemail') return item?.transcription || ''
    return null
  }, [item, itemType])

  // Resolved contact info
  const contact = resolvedData?.contact
  const clientName = resolvedData?.clientName
  const projects = resolvedData?.projects || []

  // Project picker state (for the top-level shared picker, auto-selects single)
  const [selectedProject, setSelectedProject] = useState(
    projects.length === 1 ? projects[0]._id : ''
  )

  const callerDisplay = item?.fromName || formatPhone(item?.from) || 'Unknown'
  const timestamp = item?.receivedAt || item?.time

  const toggleAction = useCallback(
    (key) => {
      setActiveAction((prev) => (prev === key ? null : key))
    },
    []
  )

  if (!item) return null

  return (
    <div style={S.panel}>
      {/* ── Back button ── */}
      <div
        style={S.backRow}
        onClick={onBack}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') onBack() }}
      >
        <IconBack size={14} />
        <span>Back to inbox</span>
      </div>

      {/* ── Header ── */}
      <div style={S.header}>
        <div
          style={{
            ...S.headerIcon,
            background: `${typeConf.color}18`,
            color: typeConf.color,
          }}
        >
          <TypeIcon size={16} />
        </div>
        <div style={S.headerInfo}>
          <div style={S.callerName}>{callerDisplay}</div>
          <div style={S.headerMeta}>
            <span
              style={{
                ...S.typeBadge,
                background: `${typeConf.color}18`,
                color: typeConf.color,
              }}
            >
              {typeConf.label}
            </span>
            {item?.from && (
              <span>{formatPhone(item.from)}</span>
            )}
            {timestamp && (
              <span>{formatAge(timestamp)}</span>
            )}
          </div>
        </div>
      </div>

      {/* ── Scrollable body ── */}
      <div style={S.scroll}>
        {/* Content */}
        <div style={S.contentBlock}>
          {contentBody ? (
            contentBody
          ) : (
            <div>
              <span style={{ color: 'var(--ink-3)', fontStyle: 'italic' }}>
                Missed call — no message
              </span>
              {item?.dialedNumber && (
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>
                  Called: {item.dialedNumber}{item.dialedName ? ` (${item.dialedName})` : ''}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Contact match */}
        <div style={S.matchRow}>
          <div style={S.matchDot(!!contact)} />
          {contact ? (
            <span>
              Matched:{' '}
              <strong style={{ color: 'var(--ink-1)' }}>
                {contact.name ||
                  `${contact.firstName || ''} ${contact.lastName || ''}`.trim() ||
                  'Contact'}
              </strong>
              {clientName && (
                <span> — {clientName}</span>
              )}
            </span>
          ) : (
            <span style={{ fontStyle: 'italic' }}>Unknown caller</span>
          )}
        </div>

        {/* Project picker (top-level, shared context) */}
        {projects.length > 1 && (
          <div style={S.projectPicker}>
            <div style={S.pickerLabel}>Project</div>
            <select
              className="sms-select"
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
            >
              <option value="">— Select project —</option>
              {projects.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.name || p.projectName || p._id}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Action bar */}
        <div style={S.actionGrid}>
          {ACTIONS.map((a) => {
            const AIc = a.icon
            const isActive = activeAction === a.key
            return (
              <div
                key={a.key}
                style={S.actionBtn(isActive)}
                onClick={() => toggleAction(a.key)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') toggleAction(a.key) }}
              >
                <AIc size={16} />
                <span>{a.label}</span>
              </div>
            )
          })}
        </div>

        {/* Action form */}
        {activeAction === 'reply' && (
          <ReplyForm
            item={item}
            url={url}
            smsNumbers={smsNumbers}
          />
        )}

        {activeAction === 'work_item' && (
          <WorkItemForm
            item={item}
            itemType={itemType}
            url={url}
            resolvedData={resolvedData}
          />
        )}

        {activeAction === 'note' && (
          <ProjectNoteForm
            item={item}
            itemType={itemType}
            url={url}
            resolvedData={resolvedData}
          />
        )}

        {activeAction === 'task' && (
          <CreateTaskForm
            item={item}
            itemType={itemType}
            url={url}
            resolvedData={resolvedData}
          />
        )}
      </div>
    </div>
  )
}
